import { RowActionMui, RowActionMuiProps } from '@app/components/table/RowActionMui';
import { useStationsFinder } from '@app/core/stations/hooks/useStations';
import { LoggerIcon } from '@app/modules/logger/LoggerIcon';
import {
  buildLoggerStreamUrl,
  LoggerStreamUrlParams,
} from '@app/modules/logger/screens/stream/helpers/build-logger-stream-url';
import { toTimestamp } from '@mi-front/core';
import { BtnLink } from '@mi-front/ui';
import React from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

const showOnStreamMessage = defineMessage({
  id: 'audit.matchesScreen.showOnStreamBtn',
  defaultMessage: 'Mostrar en stream',
});

export interface ShowOnStreamActionProps {
  /** Station slug or id used to resolve the logger stream URL. */
  station: string;
  /** Cursor / selection start time. */
  datetime: Date | string | number;
  /** Audit tab on the logger stream (e.g. livementions, audiomatches). */
  tab?: LoggerStreamUrlParams['tab'];
  /**
   * Optional selection end. When set (or durationMs is set), opens with a
   * selection range instead of a cursor.
   */
  endDatetime?: Date | string | number;
  /** Optional duration in ms from datetime; alternative to endDatetime. */
  durationMs?: number;
  /** Icon button for table row actions, or text link button. */
  variant?: 'icon' | 'link';
  /** Optional wrapper for the icon variant (defaults to RowActionMui). */
  ActionComponent?: React.ComponentType<RowActionMuiProps>;
}

/**
 * Opens the logger stream at the given station and time in a new tab.
 * Shared by audit match listings, lowerthird results, and search results.
 */
export function ShowOnStreamAction({
  station,
  datetime,
  tab,
  endDatetime,
  durationMs,
  variant = 'icon',
  ActionComponent = RowActionMui,
}: ShowOnStreamActionProps) {
  const { $t } = useIntl();
  const findStation = useStationsFinder();
  const slug = findStation(station)?.slug ?? station;
  const from = toTimestamp(datetime);

  const hasSelection = endDatetime != null || durationMs != null;
  const link = buildLoggerStreamUrl(slug, {
    tab,
    ...(hasSelection
      ? {
          selection: {
            from,
            to: endDatetime != null ? toTimestamp(endDatetime) : from + (durationMs ?? 0),
          },
        }
      : { cursor: from }),
  });

  if (variant === 'link') {
    return (
      <BtnLink size="xs" to={link} openInNewTab icon={<LoggerIcon />} className="mr-2">
        <FormattedMessage {...showOnStreamMessage} />
      </BtnLink>
    );
  }

  return (
    <ActionComponent title={$t(showOnStreamMessage)} link={link}>
      <LoggerIcon />
    </ActionComponent>
  );
}
